import { cva } from 'class-variance-authority';
import { ChevronDown } from 'lucide-react';
import { InfoTooltip } from '@/components/ui/InfoTooltip';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { computeAge } from '@/lib/age';
import type { Gender } from '@/lib/auth-types';
import { cn } from '@/lib/utils';
import { STRINGS } from '@/strings/strings';

/**
 * Shared 9-field child-profile form-block consumed by Onboarding's
 * `/onboarding/child_profile` route + Batch 9B's
 * `/account/child_profile` route. Renders fields 3-12 of Briefing
 * Ch 2 lines 429-455 — parent-name fields (1+2) stay inline at the
 * onboarding-shell level (User-entity scope, not Child-entity scope;
 * Account A-04 Profile-Page is the canonical surface for parent
 * name editing — see PWA/Offen #31).
 *
 * Presentational only — no internal state, no API calls. Caller owns
 * form state, validation, submit. The `tone` prop drives input
 * treatment (dark = onboarding navy backdrop; light = account
 * white-card backdrop).
 *
 * Per Batch 9B Bonus discipline: only `stuffedAnimalName` is collected
 * via the single "Kuscheltier" field. The DTO's `stuffedAnimalType` +
 * `stuffedAnimalTraits` columns remain dormant for future feature
 * work.
 */

export interface ChildFieldsValues {
  childName: string;
  nickname: string;
  /** ISO yyyy-mm-dd. */
  birthdate: string;
  gender: Gender | '';
  /** Single text → `stuffedAnimalName` at submit. */
  kuscheltier: string;
  /** CSV → split via `splitCSV` at submit. */
  interests: string;
  /** CSV → `referenceRoles` at submit. */
  bezugspersonen: string;
  /** CSV → `places` at submit. */
  orte: string;
  /** Free-text → `contextNotes` at submit. */
  sonstiges: string;
}

export interface ChildFieldsErrors {
  childName: string | null;
  birthdate: string | null;
  gender: string | null;
}

export interface ChildProfileFieldsProps {
  values: ChildFieldsValues;
  onChange: (patch: Partial<ChildFieldsValues>) => void;
  errors: ChildFieldsErrors;
  /** Fired on input blur for required fields — caller runs validators. */
  onBlurField: (field: 'childName' | 'birthdate' | 'gender') => void;
  tone: 'dark' | 'light';
  /** ID prefix for input + label IDs (default `'cp'` matches existing onboarding selectors). */
  idPrefix?: string;
}

const MAX_CHILD_AGE_YEARS = 13;

const labelVariants = cva('text-sm', {
  variants: {
    tone: {
      dark: 'text-dream-white',
      light: 'text-dream-navy',
    },
  },
  defaultVariants: { tone: 'dark' },
});

const inputVariants = cva('h-11', {
  variants: {
    tone: {
      dark: 'bg-dream-white text-dream-navy',
      light: 'bg-dream-white text-dream-navy',
    },
  },
  defaultVariants: { tone: 'dark' },
});

const helperTextVariants = cva('text-xs', {
  variants: {
    tone: {
      dark: 'text-dream-white/70',
      light: 'text-dream-navy/60',
    },
  },
  defaultVariants: { tone: 'dark' },
});

const helperEmphasisVariants = cva('font-medium', {
  variants: {
    tone: {
      dark: 'text-dream-white',
      light: 'text-dream-navy',
    },
  },
  defaultVariants: { tone: 'dark' },
});

const errorClass = 'text-xs text-[color:var(--color-error)]';

export function ChildProfileFields({
  values,
  onChange,
  errors,
  onBlurField,
  tone,
  idPrefix = 'cp',
}: ChildProfileFieldsProps) {
  const F = STRINGS.children.fields;

  const age = values.birthdate ? computeAge(values.birthdate) : null;
  const today = new Date().toISOString().slice(0, 10);
  const minDate = new Date(new Date().setFullYear(new Date().getFullYear() - MAX_CHILD_AGE_YEARS))
    .toISOString()
    .slice(0, 10);

  return (
    <div className="space-y-4">
      {/* Child name */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <Label htmlFor={`${idPrefix}-child-name`} className={labelVariants({ tone })}>
            {F.childName.label} *
          </Label>
          <InfoTooltip text={F.childName.tooltip} tone={tone} />
        </div>
        <Input
          id={`${idPrefix}-child-name`}
          type="text"
          value={values.childName}
          maxLength={100}
          placeholder={F.childName.placeholder}
          onChange={(e) => onChange({ childName: e.target.value })}
          onBlur={() => onBlurField('childName')}
          aria-invalid={Boolean(errors.childName)}
          className={inputVariants({ tone })}
        />
        {errors.childName && <p className={errorClass}>{errors.childName}</p>}
      </div>

      {/* Nickname */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <Label htmlFor={`${idPrefix}-nickname`} className={labelVariants({ tone })}>
            {F.nickname.label}
          </Label>
          <InfoTooltip text={F.nickname.tooltip} tone={tone} />
        </div>
        <Input
          id={`${idPrefix}-nickname`}
          type="text"
          value={values.nickname}
          maxLength={100}
          placeholder={F.nickname.placeholder}
          onChange={(e) => onChange({ nickname: e.target.value })}
          className={inputVariants({ tone })}
        />
      </div>

      {/* Birthdate */}
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-birthdate`} className={labelVariants({ tone })}>
          {F.birthdate.label} *
        </Label>
        <Input
          id={`${idPrefix}-birthdate`}
          type="date"
          value={values.birthdate}
          max={today}
          min={minDate}
          onChange={(e) => onChange({ birthdate: e.target.value })}
          onBlur={() => onBlurField('birthdate')}
          aria-invalid={Boolean(errors.birthdate)}
          className={inputVariants({ tone })}
        />
        {errors.birthdate && <p className={errorClass}>{errors.birthdate}</p>}
        {age !== null && !errors.birthdate && (
          <p className={helperTextVariants({ tone })}>
            {F.age.label}: <span className={helperEmphasisVariants({ tone })}>{age}</span>{' '}
            {F.age.suffix}
          </p>
        )}
      </div>

      {/* Gender */}
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-gender`} className={labelVariants({ tone })}>
          {F.gender.label} *
        </Label>
        <div className="relative">
          <select
            id={`${idPrefix}-gender`}
            value={values.gender}
            onChange={(e) => onChange({ gender: e.target.value as Gender | '' })}
            onBlur={() => onBlurField('gender')}
            aria-invalid={Boolean(errors.gender)}
            className={cn(
              'h-11 w-full rounded-lg bg-dream-white text-dream-navy px-2.5 pr-9 appearance-none border border-input focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 outline-none',
            )}
          >
            <option value="" disabled>
              {F.gender.placeholder}
            </option>
            <option value="male">{F.gender.optionBoy}</option>
            <option value="female">{F.gender.optionGirl}</option>
            <option value="diverse">{F.gender.optionDiverse}</option>
          </select>
          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-dream-navy pointer-events-none" />
        </div>
        {errors.gender && <p className={errorClass}>{errors.gender}</p>}
      </div>

      {/* Kuscheltier */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <Label htmlFor={`${idPrefix}-kuscheltier`} className={labelVariants({ tone })}>
            {F.kuscheltier.label}
          </Label>
          <InfoTooltip text={F.kuscheltier.tooltip} tone={tone} />
        </div>
        <Input
          id={`${idPrefix}-kuscheltier`}
          type="text"
          value={values.kuscheltier}
          maxLength={100}
          placeholder={F.kuscheltier.placeholder}
          onChange={(e) => onChange({ kuscheltier: e.target.value })}
          className={inputVariants({ tone })}
        />
      </div>

      {/* Interessen */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <Label htmlFor={`${idPrefix}-interests`} className={labelVariants({ tone })}>
            {F.interests.label}
          </Label>
          <InfoTooltip text={F.interests.tooltip} tone={tone} />
        </div>
        <Input
          id={`${idPrefix}-interests`}
          type="text"
          value={values.interests}
          placeholder={F.interests.placeholder}
          onChange={(e) => onChange({ interests: e.target.value })}
          className={inputVariants({ tone })}
        />
      </div>

      {/* Bezugspersonen */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <Label htmlFor={`${idPrefix}-bezugspersonen`} className={labelVariants({ tone })}>
            {F.bezugspersonen.label}
          </Label>
          <InfoTooltip text={F.bezugspersonen.tooltip} tone={tone} />
        </div>
        <Input
          id={`${idPrefix}-bezugspersonen`}
          type="text"
          value={values.bezugspersonen}
          placeholder={F.bezugspersonen.placeholder}
          onChange={(e) => onChange({ bezugspersonen: e.target.value })}
          className={inputVariants({ tone })}
        />
      </div>

      {/* Orte */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <Label htmlFor={`${idPrefix}-orte`} className={labelVariants({ tone })}>
            {F.orte.label}
          </Label>
          <InfoTooltip text={F.orte.tooltip} tone={tone} />
        </div>
        <Input
          id={`${idPrefix}-orte`}
          type="text"
          value={values.orte}
          placeholder={F.orte.placeholder}
          onChange={(e) => onChange({ orte: e.target.value })}
          className={inputVariants({ tone })}
        />
      </div>

      {/* Sonstiges */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <Label htmlFor={`${idPrefix}-sonstiges`} className={labelVariants({ tone })}>
            {F.sonstiges.label}
          </Label>
          <InfoTooltip text={F.sonstiges.tooltip} tone={tone} />
        </div>
        <textarea
          id={`${idPrefix}-sonstiges`}
          rows={3}
          value={values.sonstiges}
          maxLength={1000}
          placeholder={F.sonstiges.placeholder}
          onChange={(e) => onChange({ sonstiges: e.target.value })}
          className="w-full rounded-lg bg-dream-white text-dream-navy px-2.5 py-2 border border-input focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 outline-none text-base md:text-sm resize-none"
        />
      </div>
    </div>
  );
}
